import { Link } from "@tanstack/react-router";

import { CihSectionHeader } from "~/components/cih/section-header";
import { MarkdownContent } from "~/components/markdown-content";
import { Button, ButtonLink } from "~/components/ui/button";
import { useOfferCheckout } from "~/hooks/use-offer-checkout";
import { pickOfferCta, type CtaDescriptor } from "~/lib/offer/cta";
import { MembershipPlan, type OfferStatus } from "~/lib/schemas/db-enums";
import { formatPrice } from "~/lib/shared/format-price";

type OfferBenefit = { id: string; title: string; order: number };

type Offer = {
  id: string;
  type: string;
  status: OfferStatus;
  preHeadline: string | null;
  headline: string | null;
  description: string | null;
  benefits?: OfferBenefit[];
  applicationDeadline: Date | string | null;
  cohortStartDate: Date | string | null;
  price: number | null;
  originalPrice: number | null;
  currency: string | null;
  directSaleEnabled: boolean | null;
  satisfactionGuaranteeText: string | null;
};

type OtherOffer = {
  id: string;
  preHeadline?: string | null;
  headline: string | null;
  description: string | null;
};

export type OfferSectionProps = {
  coach: {
    username: string;
    membershipPlan: string | null;
    profile: {
      ctaButtonText: string | null;
      contactMode: "booking" | "messaging" | null;
      isPreGeneratedProfile: boolean;
    };
  };
  mainOffer: Offer | null;
  otherOffers: OtherOffer[];
  sectionNumber: number;
};

function formatDateShort(date: Date | string): string {
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

type BuyButtonProps = {
  offerId: string;
  label: string;
};

function BuyButton({ offerId, label }: BuyButtonProps) {
  const { createCheckout } = useOfferCheckout();

  return (
    <Button
      variant="primary"
      size="default"
      disabled={createCheckout.isPending}
      onClick={() => createCheckout.mutate(offerId)}
    >
      {createCheckout.isPending ? "Redirecting…" : label}
    </Button>
  );
}

type CtaButtonProps = {
  cta: CtaDescriptor;
};

function CtaButton({ cta }: CtaButtonProps) {
  if (cta.kind === "buy") {
    return <BuyButton offerId={cta.offerId} label={cta.label} />;
  }
  return (
    <ButtonLink href={cta.href} variant="primary" size="default">
      {cta.label}
    </ButtonLink>
  );
}

export function CoachOfferSection({
  coach,
  mainOffer,
  otherOffers,
  sectionNumber,
}: OfferSectionProps) {
  if (!mainOffer?.headline?.trim()) return null;

  const cta = pickOfferCta({ offer: mainOffer, coach });

  const sortedBenefits = mainOffer.benefits
    ? [...mainOffer.benefits].sort((a, b) => a.order - b.order)
    : [];

  const dates = [
    mainOffer.applicationDeadline
      ? `Apply by ${formatDateShort(mainOffer.applicationDeadline)}`
      : null,
    mainOffer.cohortStartDate
      ? `Starts ${formatDateShort(mainOffer.cohortStartDate)}`
      : null,
  ].filter((s): s is string => Boolean(s));

  const showOtherOffers =
    coach.membershipPlan === MembershipPlan.ProPlus && otherOffers.length > 0;

  return (
    <section id="coaching" className="border-t border-gray-200">
      <CihSectionHeader
        label={`${String(sectionNumber).padStart(2, "0")} · The coaching`}
        title={mainOffer.headline ?? "How I work with clients"}
      />
      <div className="mx-auto max-w-7xl px-4 pb-14 md:pb-20 xl:px-0">
        {mainOffer.preHeadline && (
          <p className="text-sm font-medium tracking-widest text-brand-600 uppercase">
            {mainOffer.preHeadline}
          </p>
        )}

        {mainOffer.description && (
          <div className="mt-6 max-w-prose text-gray-700">
            <MarkdownContent ugc content={mainOffer.description} />
          </div>
        )}

        {sortedBenefits.length > 0 && (
          <ul className="mt-8 space-y-3">
            {sortedBenefits.map((benefit) => (
              <li key={benefit.id} className="flex items-start gap-3">
                <span className="mt-0.5 size-5 shrink-0 rounded-full bg-brand-100 text-center text-xs leading-5 text-brand-700">
                  ✓
                </span>
                <span className="text-gray-700">{benefit.title}</span>
              </li>
            ))}
          </ul>
        )}

        {dates.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-4">
            {dates.map((label) => (
              <span
                key={label}
                className="rounded-full bg-amber-50 px-4 py-1.5 text-sm font-medium text-amber-800"
              >
                {label}
              </span>
            ))}
          </div>
        )}

        {mainOffer.directSaleEnabled &&
          mainOffer.price &&
          mainOffer.currency && (
            <div className="mt-8">
              <div className="flex flex-wrap items-baseline gap-3">
                {mainOffer.originalPrice &&
                  mainOffer.originalPrice > mainOffer.price && (
                    <span className="text-lg text-gray-400 line-through">
                      {formatPrice(mainOffer.originalPrice, mainOffer.currency)}
                    </span>
                  )}
                <span className="font-heading text-3xl font-medium">
                  {formatPrice(mainOffer.price, mainOffer.currency)}
                </span>
              </div>
            </div>
          )}

        {/* Pre-generated (unclaimed) profiles have no account behind them:
            /contact and /book both redirect straight back to the profile, so
            the claim banner must stay the only CTA (same rule as the hero and
            closing band via shouldShowCoachCTAs). */}
        {!coach.profile.isPreGeneratedProfile && (
          <div className="mt-8">
            <CtaButton cta={cta} />
          </div>
        )}

        {mainOffer.satisfactionGuaranteeText && (
          <p className="mt-4 text-sm text-gray-500">
            {mainOffer.satisfactionGuaranteeText}
          </p>
        )}

        {showOtherOffers && (
          <div className="mt-16">
            <h2 className="font-heading text-xl font-medium text-gray-900">
              Also available
            </h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {otherOffers.map((offer) => (
                <Link
                  key={offer.id}
                  to="/$username/offer/$offerId"
                  params={{ username: coach.username, offerId: offer.id }}
                  className="group rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:border-brand-300 hover:shadow-md"
                >
                  {offer.preHeadline && (
                    <p className="mb-1 text-xs font-medium tracking-wider text-brand-600 uppercase">
                      {offer.preHeadline}
                    </p>
                  )}
                  <h3 className="font-medium text-gray-900 group-hover:text-brand-700">
                    {offer.headline ?? "Offer"}
                  </h3>
                  {offer.description && (
                    <p className="mt-2 line-clamp-2 text-sm text-gray-500">
                      {offer.description}
                    </p>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
